import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as R from 'ramda'
import { Defaults, type Nil, RoleName } from '@pbt/pbt-ui-components'

import {
  CreatedConversationFlow,
  type CreatedConversationsInfoDialogProps,
} from '~/components/dashboard/communications/common/CreatedConversationsInfoDialog'
import {
  ConversationCategory,
  INITIAL_COMMUNICATION_FILTERS,
} from '~/constants/communications'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { BaseRoute } from '~/constants/routes'
import type { TableFilter } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'
import useHasRole from '~/utils/useHasRole'

import type { RootState } from '..'
import {
  fetchClientConversationsList,
  fetchConversationsList,
  fetchMoreItemsForClientConversationsList,
  fetchMoreItemsForConversationsList,
  setConversationsListFilters,
} from '../actions/conversations'
import { getConstantsIsLoading, getFeatureToggle } from '../reducers/constants'
import {
  getAreConversationsCreated,
  getConversationsError,
  getConversationsListFilters,
} from '../reducers/conversations'

export function useConversationList({
  clientId,
  patientId,
  queryFrom,
  queryTo,
  fillPrecedingWithNull,
}: {
  clientId?: string | Nil
  fillPrecedingWithNull?: boolean
  patientId?: string | Nil
  queryFrom?: number
  queryTo?: number
}) {
  const dispatch = useDispatch()

  const filters = useSelector(getConversationsListFilters)
  const isConstantsLoading = useSelector(getConstantsIsLoading)
  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )

  const applyFilter = (filterName: string, filterValue: TableFilter) => {
    dispatch(
      setConversationsListFilters(
        filterValue.value
          ? { ...filters, [filterName]: filterValue }
          : R.omit([filterName], filters),
      ),
    )
  }

  const clearFilters = () => {
    const initialFilters: { [key: string]: { value: any } } =
      INITIAL_COMMUNICATION_FILTERS

    if (isSuppressAllSystemMessagesEnabled) {
      initialFilters.excludeSystemMessages = { value: true }
    }

    dispatch(setConversationsListFilters(initialFilters))
  }

  useEffect(() => {
    const initialFilters: { [key: string]: { value: any } } =
      isSuppressAllSystemMessagesEnabled
        ? {
            ...INITIAL_COMMUNICATION_FILTERS,
            excludeSystemMessages: { value: true },
          }
        : INITIAL_COMMUNICATION_FILTERS

    dispatch(
      setConversationsListFilters({
        ...initialFilters,
        ...filters,
        category: {
          value: filters.category?.value || ConversationCategory.INBOX,
        },
        patientIds: { value: null },
      }),
    )
  }, [isSuppressAllSystemMessagesEnabled])

  useEffect(() => {
    const action = clientId
      ? // TODO: add endpoint for fetching client/patient conversations based on ? patientId
        fetchClientConversationsList(
          clientId,
          queryFrom || 0,
          queryTo || Defaults.CONVERSATIONS_BATCH_LOAD_COUNT,
          fillPrecedingWithNull,
        )
      : fetchConversationsList(
          queryFrom || 0,
          queryTo || Defaults.CONVERSATIONS_BATCH_LOAD_COUNT,
          fillPrecedingWithNull,
        )
    dispatch(action)
  }, [filters, clientId, isConstantsLoading])

  const loadMoreItems = (startIndex: number, stopIndex?: number) => {
    // loadMoreItems has trouble recognizing unloaded items at the start of the list
    // so if we're asking for a startIndex close to 0, we start from 0
    const offset = startIndex < 10 ? 0 : startIndex
    // need to calculate limit based on offset and stopIndex
    const limit = stopIndex
      ? stopIndex - offset + 1
      : Defaults.CONVERSATIONS_BATCH_LOAD_COUNT
    const action = clientId
      ? fetchMoreItemsForClientConversationsList(clientId, offset, limit)
      : fetchMoreItemsForConversationsList(offset, limit)
    dispatch(action)
  }

  return { loadMoreItems, clearFilters, applyFilter, filters }
}

export function useCreatedConversationsInfo({
  getIsConversationCreating,
  onConversationCreationSuccess,
  onConversationCreationFailure,
  onViewConversation = R.F,
  displayDefaultSuccessResult = true,
  createdInfoDialogProps = {},
}: {
  createdInfoDialogProps?: Partial<CreatedConversationsInfoDialogProps>
  displayDefaultSuccessResult?: boolean
  getIsConversationCreating: (state: RootState) => boolean
  onConversationCreationFailure?: (error: string) => void
  onConversationCreationSuccess?: () => void
  onViewConversation?: (conversationId: string) => void
}) {
  const navigate = useNavigate()
  const areConversationsCreated = useSelector(getAreConversationsCreated)
  const latestConversationError = useSelector(getConversationsError)
  const [openCreatedConversationsInfoDialog] = useDialog(
    DialogNames.CREATED_CONVERSATIONS_INFO_DIALOG,
  )

  const handleViewConversation = (conversationId: string) => {
    navigate(`${BaseRoute.COMMUNICATIONS}/${conversationId}`)
    onViewConversation(conversationId)
  }

  const conversationCreationSuccess = () => {
    if (displayDefaultSuccessResult) {
      openCreatedConversationsInfoDialog({
        ...createdInfoDialogProps,
        flow: CreatedConversationFlow.PLAIN_MESSAGE_SENT,
        handleViewConversation,
      })
    }

    if (onConversationCreationSuccess) {
      onConversationCreationSuccess()
    }
  }

  const conversationCreationFail = () => {
    if (onConversationCreationFailure) {
      onConversationCreationFailure(latestConversationError as string)
    }
  }

  const onConversationCreated = () => {
    if (areConversationsCreated) {
      conversationCreationSuccess()
    } else {
      conversationCreationFail()
    }
  }

  return useCloseAfterCreation(onConversationCreated, getIsConversationCreating)
}

export function useIncludeMedicalRecord() {
  const isMarkMessagesAsMedicalEnabled = useSelector(
    getFeatureToggle(FeatureToggle.MARK_MESSAGES_AS_MEDICAL),
  )
  const isVeterinarian1 = useHasRole(RoleName.Veterinarian)
  const isVeterinarian2 = useHasRole(RoleName.Veterinarian2)
  const isVeterinarian = isVeterinarian1 || isVeterinarian2

  const isVetTech = useHasRole(RoleName.VetTech)
  const isVetAssistant = useHasRole(RoleName.VetAssistant)
  const isVetTechOrAssistant = isVetTech || isVetAssistant

  return {
    shouldShowCheckbox:
      isMarkMessagesAsMedicalEnabled &&
      (isVeterinarian || isVetTechOrAssistant),
    defaultCheckboxValue: isMarkMessagesAsMedicalEnabled && isVeterinarian,
  }
}
